import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import supabase from '../../utils/supabaseClient';
import LoadingScreen from '../../components/LoadingScreen';

const ProductInputPage = () => {
  const { id } = useParams(); // Product ID from URL
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  
  const [productData, setProductData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Form state
  const [name, setName] = useState('');
  const [conceptDescription, setConceptDescription] = useState('');
  const [conceptValidationMethod, setConceptValidationMethod] = useState('');
  const [indicationsOfUse, setIndicationsOfUse] = useState('');
  
  // Fetch product data function wrapped in useCallback
  const fetchProductData = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      
      if (data) {
        setProductData(data);
        setName(data.name || '');
        setConceptDescription(data.concept_description || '');
        setConceptValidationMethod(data.concept_validation_method || '');
        setIndicationsOfUse(data.indications_of_use || '');
      }
    } catch (err) {
      console.error('Error fetching product data:', err);
      setError('Failed to load product data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [id]); // Include id in the dependency array
  
  // Fetch product data on component mount
  useEffect(() => {
    if (user) {
      fetchProductData();
    }
  }, [user, id, fetchProductData]); // Add fetchProductData to the dependency array
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsSaving(true);
      setError(null);
      
      const currentDate = new Date().toISOString();
      
      const { error: updateError } = await supabase
        .from('products')
        .update({
          name,
          concept_description: conceptDescription,
          concept_validation_method: conceptValidationMethod,
          indications_of_use: indicationsOfUse,
          updated_at: currentDate
        })
        .eq('id', id);
      
      if (updateError) throw updateError;
      
      // Redirect to the stage-specific details page
      navigate(`/products/${id}/details/${productData.development_stage.toLowerCase()}`);
      
    } catch (err) {
      console.error('Error updating product:', err);
      setError('Failed to update product. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Handle development path analysis
  const handleAnalyzeClick = async () => {
    try {
      setIsSaving(true);
      
      // First save the current form data
      const { error: saveError } = await supabase
        .from('products')
        .update({
          name,
          concept_description: conceptDescription,
          concept_validation_method: conceptValidationMethod,
          indications_of_use: indicationsOfUse,
          updated_at: new Date()
        })
        .eq('id', id);
      
      if (saveError) throw saveError;
      
      // TODO: Add API call to analyze development path using OpenAI
      // This will be implemented in the next phase
      
      // Navigate to financial input
      navigate(`/products/${id}/financial`);
    } catch (err) {
      console.error('Error during analysis:', err);
      setError('Failed to analyze development path. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  
  if (loading || isLoading) {
    return <LoadingScreen message="Loading..." />;
  }
  
  if (!productData) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6" role="alert">
          <p>Product not found. Please check the URL and try again.</p>
        </div>
        <Link to="/products" className="btn-primary">
          Back to Products
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Link to={`/products/${id}`} className="text-primary-600 hover:text-primary-700 flex items-center">
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Back to Product Details
        </Link>
      </div>
      
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Update Product Details</h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6" role="alert">
          <p>{error}</p>
        </div>
      )}
      
      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="name" className="block text-gray-700 text-sm font-bold mb-2">
              Product Name*
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter the product name"
              required
            />
          </div>
          
          <div className="mb-4">
            <label htmlFor="conceptDescription" className="block text-gray-700 text-sm font-bold mb-2">
              Concept Description*
            </label>
            <textarea
              id="conceptDescription"
              value={conceptDescription}
              onChange={(e) => setConceptDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder="Describe the product concept"
              rows="4"
              required
            ></textarea>
          </div>
          
          <div className="mb-4">
            <label htmlFor="conceptValidationMethod" className="block text-gray-700 text-sm font-bold mb-2">
              Concept Validation Method*
            </label>
            <textarea
              id="conceptValidationMethod"
              value={conceptValidationMethod}
              onChange={(e) => setConceptValidationMethod(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder="Describe how the concept will be validated"
              rows="3"
              required
            ></textarea>
          </div>
          
          <div className="mb-6">
            <label htmlFor="indicationsOfUse" className="block text-gray-700 text-sm font-bold mb-2">
              Indications of Use*
            </label>
            <textarea
              id="indicationsOfUse"
              value={indicationsOfUse}
              onChange={(e) => setIndicationsOfUse(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder="Describe the intended indications of use"
              rows="3"
              required
            ></textarea>
          </div>
          
          <div className="flex justify-between mt-6">
            <button
              type="submit"
              className="btn-primary"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save and Continue'}
            </button>
            
            <button
              type="button"
              onClick={handleAnalyzeClick}
              className="btn-secondary"
              disabled={isSaving}
            >
              {isSaving ? 'Processing...' : 'Analyze Development Path'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductInputPage; 